import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import trim from 'lodash/trim';
import { log } from '../shared/utils/log';

/**
 * Creates a validation from a predicate.
 * The validation returns true if the predicate returns a truthy value,
 * or the given error message if the predicate returns a falsy value.
 */
const satisfies = <T>(predicate: (obj: T | null | undefined) => boolean, message: string): Validation<T> => {
    return obj => (predicate(obj) ? true : message);
};

/**
 * Validates whether a value is present, that is not null, not undefined and not NaN.
 */
export const isPresent = satisfies<any>(obj => {
    if (obj === null) {
        return false;
    }

    if (obj === undefined) {
        return false;
    }

    return !(typeof obj === 'number' && isNaN(obj));
}, 'is not present');

/**
 * Validates whether a value is a non empty string.
 * NOTE: If the given value is not a string, this validation returns true!
 */
export const isNotBlank = satisfies<any>(obj => {
    if (typeof obj !== 'string') {
        return true;
    }

    return !isEmpty(trim(obj));
}, 'is blank');

/**
 * Validates whether a value is a non empty array.
 * NOTE: If the given value is not a string, this validation returns true!
 */
export const isNotEmpty = satisfies<any>(obj => {
    if (!Array.isArray(obj)) {
        return true;
    }

    return obj.length > 0;
}, 'is empty');

/**
 * Validates whether a value is a positive number.
 * NOTE: If the given value is not a numeric value, this validation returns true!
 */
export const isPositive = satisfies<any>(obj => {
    if (typeof obj !== 'number') {
        return true;
    }
    return obj >= 0;
}, 'is not positive');

/**
 * Creates a validation that validates whether a value is equal to the given expected value.
 * NOTE: If the value being validated is not present (null or undefined), this validation returns true!
 */
export const isEqualTo = (expected: any) =>
    satisfies((object: any) => {
        if (object === null || object === undefined) {
            return true;
        }
        return isEqual(object, expected);
    }, `is not equal to ${expected}`);

/**
 * Combines the given validations into one validation.
 * The resulting validation succeeds if one or more of the given validation succeed for a given value.
 * The resulting validation fails if all of the given validations fail for a given value.
 */
export const validateOneOf = <T>(...validations: ReadonlyArray<Validation<T>>): Validation<T> => {
    return obj => {
        return validations
            .map(validation => validation(obj))
            .reduce((overall, result) => {
                if (overall === true || result === true) {
                    return true;
                }
                return overall === '' ? result : combineValidationResults(overall, result);
            }, '');
    };
};

/**
 * Creates a validation that checks whether a value is equal to one of the values of the given object (or enum!).
 */
export const isValueOf = <T>(expected: { [name: string]: T }): Validation<any> => {
    return validateOneOf(...Object.values(expected).map(value => isEqualTo(value)));
};

/**
 * Creates a validation that checks whether a value is equal to one of the given values.
 */
export const isOneOf = <T>(...expected: ReadonlyArray<T>): Validation<any> => {
    return validateOneOf(...expected.map(value => isEqualTo(value)));
};

type ValidationMessage = string | ReadonlyArray<string>;
type ValidationResult = true | ValidationMessage;
// eslint-disable-next-line
type Validation<T> = (value: any) => ValidationResult;

export const validateAndLogOnErrors = <T>(validation: Validation<T>, object: T): boolean => {
    const validationResult = validation(object);
    logValidationResultIfError(validationResult, object);
    return validationResult === true;
};

export const logValidationResultIfError = (result: ValidationResult, object: any) => {
    if (result !== true) {
        const message = Array.isArray(result) ? result.join(', ') : result;
        log.error(message?.toString(), object);
    }
};

export const validateField = <T, K extends keyof T = keyof T>(
    field: K,
    validation: Validation<T[K]>
): Validation<T> => {
    return obj => {
        if (!obj) {
            return true;
        }

        const result = validation(obj[field]);
        return result === true ? true : `invalid field "${field.toString()}" ${result}`;
    };
};

export const validateAll = <T>(...validations: ReadonlyArray<Validation<T>>): Validation<T> => {
    return obj => validations.map(v => v(obj)).reduce(combineValidationResults, true);
};

export function validateEach<E>(validation: Validation<E>): Validation<ReadonlyArray<any>> {
    return arr => {
        if (!Array.isArray(arr)) {
            return true;
        }

        return arr.map(it => validation(it)).reduce(combineValidationResults, true);
    };
}

const combineValidationResults = (left: ValidationResult, right: ValidationResult): ValidationResult => {
    if (left === true && right === true) return true;
    if (left === true) return right;
    if (right === true) return left;

    if (Array.isArray(left) && Array.isArray(right)) {
        return [...left, ...right];
    }

    if (Array.isArray(left)) {
        return [...left, right];
    }

    if (Array.isArray(right)) {
        return [left, ...right];
    }

    return [left as string, right as string];
};
